import dayjs from "dayjs";
import {
  LineType,
  OrderLine,
  OrderToken,
  TokenStatus,
} from "@smeetz/order-common";
import { OrderLinePublicType } from "@smeetz/orders";
import { getProductCategoriesByUuids } from "@/dexie/product-category";
import { getTimeslotById } from "@/dexie/timeslots";
import { IProductCategory } from "@/types/db/product-category";
import {
  BookingTeamsStatus,
  ITeam,
  ITeamSetting,
  TeamLevel,
} from "@/interfaces/teams/teams";

// Teams (pairing step) on the new order logic. The legacy step worked from the
// booking's pss list, one row per ticket. On an order a line's quantity covers
// several tickets, but the orders package generates one token per sold unit at
// completion, so the token is the unit of "ticket" here: teams are sliced from
// tokens and each team keeps its own token values for the scan after pairing.

/** A sold ticket line resolved to its catalog category and numeric price id. */
export interface IResolvedTeamLine {
  line: OrderLine;
  category: IProductCategory;
  priceId: number;
}

function isTicketSaleLine(line: OrderLine): boolean {
  return (
    line.lineType === LineType.SaleLine &&
    line.itemType !== OrderLinePublicType.RETAIL
  );
}

/**
 * Resolves the order's ticket lines to their catalog category and numeric
 * price id. NOL lines carry catalog uuids (`buildOrderLineDto` stores
 * `price.uuid` as `priceId`, and `categoryId` is the category uuid), while the
 * team settings use the numeric ids, so the resolution goes through the Dexie
 * catalog like `syncOrderAvailabilities`. Lines whose category or price is not
 * in the local catalog are left out.
 */
export async function resolveTeamLines(
  lines: OrderLine[]
): Promise<IResolvedTeamLine[]> {
  const ticketLines = lines.filter(isTicketSaleLine);
  const categoryUuids = [
    ...new Set(ticketLines.map((line) => line.categoryId)),
  ];
  const categories = await getProductCategoriesByUuids(categoryUuids);
  const categoriesByUuid = new Map(categories.map((pc) => [pc.uuid, pc]));

  const resolved: IResolvedTeamLine[] = [];
  for (const line of ticketLines) {
    const category = categoriesByUuid.get(line.categoryId);
    const priceId = category?.prices?.find(
      (price) => price.uuid === line.priceId
    )?.id;
    if (category && priceId) {
      resolved.push({ line, category, priceId });
    }
  }
  return resolved;
}

/**
 * Team settings of the categories sold on the order, one per category. An
 * empty list means the order does not require teams.
 */
export function getOrderTeamSettings(
  resolvedLines: IResolvedTeamLine[]
): ITeamSetting[] {
  const settings = new Map<number, ITeamSetting>();
  for (const { category } of resolvedLines) {
    if (category.teamSetting && !settings.has(category.id)) {
      settings.set(category.id, category.teamSetting);
    }
  }
  return [...settings.values()];
}

/** Active tokens whose line sells a price counted by its category's team setting. */
function countedTokens(
  tokens: OrderToken[],
  resolvedLines: IResolvedTeamLine[],
  settings: ITeamSetting[]
): { token: OrderToken; resolved: IResolvedTeamLine }[] {
  const linesById = new Map(resolvedLines.map((r) => [r.line.id, r]));
  const countedPspByCategory = new Map(
    settings.map((s) => [s.categoryId, new Set(s.countedPsp)])
  );
  const counted: { token: OrderToken; resolved: IResolvedTeamLine }[] = [];
  for (const token of tokens) {
    if (token.status !== TokenStatus.ACTIVE) continue;
    const resolved = linesById.get(token.orderLineId);
    if (!resolved) continue;
    if (countedPspByCategory.get(resolved.category.id)?.has(resolved.priceId)) {
      counted.push({ token, resolved });
    }
  }
  return counted;
}

/**
 * Number of tickets the teams must cover: the active tokens of the counted
 * prices, the order-side equivalent of the legacy counted pss list.
 */
export function countTeamTickets(
  tokens: OrderToken[],
  resolvedLines: IResolvedTeamLine[],
  settings: ITeamSetting[]
): number {
  return countedTokens(tokens, resolvedLines, settings).length;
}

/**
 * `YYYY-MM-DD HH:mm:ss` from a wall-clock string, whether it comes as
 * `2026-03-12 13:30:00` or as `2026-03-12T13:30:00+01:00`. The offset is
 * dropped on purpose: the backend stores and compares the venue wall clock.
 */
function toTeamDate(wallClock: string): string {
  return wallClock.slice(0, 19).replace("T", " ");
}

/**
 * Venue wall clock of a line, from its `startDateTimeLocal` /
 * `endDateTimeLocal`, falling back to the Dexie timeslot's tz dates when the
 * line carries no local time. The UTC `startDateTime` is never used.
 */
async function lineTeamDates(
  line: OrderLine
): Promise<{ start: string; end: string } | null> {
  if (line.startDateTimeLocal && line.endDateTimeLocal) {
    return {
      start: toTeamDate(line.startDateTimeLocal),
      end: toTeamDate(line.endDateTimeLocal),
    };
  }
  if (line.timeSlotId) {
    const timeslot = await getTimeslotById(line.timeSlotId);
    if (timeslot?.startDateTimeTz && timeslot.endDateTimeTz) {
      return {
        start: toTeamDate(timeslot.startDateTimeTz),
        end: toTeamDate(timeslot.endDateTimeTz),
      };
    }
  }
  return null;
}

/**
 * Builds the suggested teams of an order: the counted tokens grouped by
 * category and timeslot, each group sliced into teams of at most
 * `maxParticipants`, with the team's token values kept for the scan after
 * pairing. Teams come out in timeslot order.
 */
export async function buildOrderTeams(
  tokens: OrderToken[],
  resolvedLines: IResolvedTeamLine[],
  settings: ITeamSetting[]
): Promise<ITeam[]> {
  const groups = new Map<
    string,
    { resolved: IResolvedTeamLine; tokens: OrderToken[] }
  >();
  for (const { token, resolved } of countedTokens(
    tokens,
    resolvedLines,
    settings
  )) {
    const key = `${resolved.category.id}|${resolved.line.timeSlotId ?? ""}`;
    const group = groups.get(key);
    if (group) {
      group.tokens.push(token);
    } else {
      groups.set(key, { resolved, tokens: [token] });
    }
  }

  const startOf = (group: { resolved: IResolvedTeamLine }) =>
    dayjs(group.resolved.line.startDateTime ?? 0).valueOf();
  const orderedGroups = [...groups.values()].sort(
    (a, b) => startOf(a) - startOf(b)
  );

  const teams: ITeam[] = [];
  for (const group of orderedGroups) {
    const setting = settings.find(
      (s) => s.categoryId === group.resolved.category.id
    );
    const dates = await lineTeamDates(group.resolved.line);
    if (!setting || !dates) continue;

    for (
      let offset = 0;
      offset < group.tokens.length;
      offset += setting.maxParticipants
    ) {
      const slice = group.tokens.slice(
        offset,
        offset + setting.maxParticipants
      );
      teams.push({
        name: "",
        roomPlayers: slice.length,
        startDateTime: dates.start,
        endDateTime: dates.end,
        nfcTag: "",
        paired: false,
        tokens: slice.map((token) => token.token),
        teamLevel: TeamLevel.MEDIUM,
      });
    }
  }
  return teams;
}

/**
 * Status of the order's teams for the completed screen and the Orders table:
 * no team yet, not enough players for the counted tickets, or complete.
 */
export function getTeamsStatus(
  teams: ITeam[],
  countedTickets: number
): BookingTeamsStatus {
  if (!teams.length) {
    return BookingTeamsStatus.NotStarted;
  }
  const players = teams.reduce((sum, team) => sum + team.roomPlayers, 0);
  return countedTickets > players
    ? BookingTeamsStatus.InProgress
    : BookingTeamsStatus.Completed;
}

/**
 * Whether two team dates name the same moment. The built teams and the ones
 * read from the backend both carry venue wall clock, but one may differ in
 * formatting, so the merge compares instants rather than strings.
 */
export function isSameTeamMoment(a: string, b: string): boolean {
  return dayjs(a).isSame(dayjs(b));
}

/**
 * Merges the suggested teams with the ones already paired on the backend. A
 * fetched team that matches a suggested one (same dates and player count)
 * replaces it and takes over its token slice, so pairing an already-known team
 * scans its own tokens. Fetched teams with no match are the ones whose tickets
 * were removed since; they are returned separately for cancellation.
 */
export function mergeOrderTeams(
  suggested: ITeam[],
  fetched: ITeam[]
): { teams: ITeam[]; toCancel: ITeam[] } {
  const remaining = [...suggested];
  const teams: ITeam[] = [];
  const toCancel: ITeam[] = [];
  for (const fetchedTeam of fetched) {
    const index = remaining.findIndex(
      (team) =>
        team.roomPlayers === fetchedTeam.roomPlayers &&
        isSameTeamMoment(team.startDateTime, fetchedTeam.startDateTime) &&
        isSameTeamMoment(team.endDateTime, fetchedTeam.endDateTime)
    );
    if (index === -1) {
      toCancel.push(fetchedTeam);
      continue;
    }
    teams.push({ ...fetchedTeam, tokens: remaining[index].tokens });
    remaining.splice(index, 1);
  }
  return { teams: [...teams, ...remaining], toCancel };
}
